import * as React from 'react';
import { useEffect, useState } from 'react';
import { generateUrl } from '../../../Utilities/api';
function scoreToBand(score) {
    if (score < 40)
        return 'red';
    if (score < 70)
        return 'yellow';
    return 'green';
}
// ── Constants ──────────────────────────────────────────────────────────────
const BRAND = {
    blue: '#00537e',
    blueDeep: '#004360',
    tint: '#EEF4F7',
    red: '#f93f43',
    yellow: '#efe300',
    green: '#138f38',
};
const BAND_COLORS = {
    red: BRAND.red,
    yellow: BRAND.yellow,
    green: BRAND.green,
};
const CATEGORY_LABELS = {
    kraft: 'Kraft',
    beweglichkeit: 'Beweglichkeit',
    gleichgewicht: 'Gleichgewicht',
    koerperzusammensetzung: 'Körperzusammensetzung',
    schmerzfreiheit: 'Schmerzfreiheit',
    stressfreiheit: 'Stressfreiheit',
    energie: 'Energie',
    ernaehrung: 'Ernährung',
    mental: 'Mental',
    environment: 'Umfeld',
    regeneration: 'Regeneration',
    training: 'Training',
};
const IST_ZUSTAND_CATEGORIES = [
    'kraft', 'beweglichkeit', 'gleichgewicht',
    'koerperzusammensetzung', 'schmerzfreiheit', 'stressfreiheit', 'energie',
];
const VERHALTEN_CATEGORIES = ['ernaehrung', 'mental', 'environment', 'regeneration', 'training'];
// ── Sub-components ─────────────────────────────────────────────────────────
function ScoreRing({ score, size = 56, strokeWidth = 6 }) {
    const band = scoreToBand(score);
    const color = BAND_COLORS[band];
    const r = (size - strokeWidth) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (score / 100) * circ;
    return (<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth}/>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth} strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"/>
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize={size * 0.26} fontWeight="700" fill="#111827" style={{ transform: 'rotate(90deg)', transformOrigin: '50% 50%' }}>
                {score}
            </text>
        </svg>);
}
function ScoreRingPlaceholder({ size = 56 }) {
    return (<div className="rounded-full bg-gray-100 animate-pulse" style={{ width: size, height: size }}/>);
}
function BandBar({ score }) {
    const band = scoreToBand(score);
    return (<div className="h-1.5 rounded-full bg-gray-100 w-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: BAND_COLORS[band] }}/>
        </div>);
}
// ── IST-Zustand Row ─────────────────────────────────────────────────────────
function IstZustandRow({ category, entry, isLever, isFocus }) {
    const band = scoreToBand(entry.score);
    return (<div className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={isFocus ? { borderLeft: `3px solid ${BRAND.blue}`, backgroundColor: BRAND.tint } : { borderLeft: '3px solid transparent' }}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 mb-0.5">
                    <span className="text-xs font-medium text-gray-700 truncate">
                        {CATEGORY_LABELS[category] ?? category}
                    </span>
                    {isLever && (<span className="text-xs font-semibold px-1 py-0.5 rounded-full" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>Hebel</span>)}
                </div>
                <BandBar score={entry.score}/>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <span className="inline-block rounded-full w-1.5 h-1.5" style={{ backgroundColor: BAND_COLORS[band] }}/>
                <span className="text-xs font-bold tabular-nums" style={{ color: BRAND.blue }}>{entry.score}</span>
            </div>
        </div>);
}
function IstZustandRowPlaceholder() {
    return (<div className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{ borderLeft: '3px solid transparent' }}>
            <div className="flex-1">
                <div className="h-2.5 w-20 bg-gray-100 rounded animate-pulse mb-1.5"/>
                <div className="h-1.5 bg-gray-100 rounded-full animate-pulse"/>
            </div>
            <div className="w-6 h-3 bg-gray-100 rounded animate-pulse"/>
        </div>);
}
// ── Verhalten Tile ──────────────────────────────────────────────────────────
function VerhaltenTile({ category, entry, isFocus }) {
    const band = scoreToBand(entry.score);
    return (<div className="bg-white rounded-2xl p-4 flex flex-col items-center gap-3" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', ...(isFocus ? { outline: `2px solid ${BRAND.blue}` } : {}) }}>
            <ScoreRing score={entry.score} size={72} strokeWidth={6}/>
            <div className="text-center">
                <span className="text-sm font-semibold text-gray-700 leading-tight">{CATEGORY_LABELS[category] ?? category}</span>
                <div className="flex items-center justify-center gap-1 mt-0.5">
                    <span className="inline-block rounded-full w-1.5 h-1.5" style={{ backgroundColor: BAND_COLORS[band] }}/>
                    <span className="text-xs text-gray-400">{entry.score} / 100</span>
                </div>
            </div>
        </div>);
}
function VerhaltenTilePlaceholder() {
    return (<div className="bg-white rounded-2xl p-4 flex flex-col items-center gap-3" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
            <ScoreRingPlaceholder size={72}/>
            <div className="h-3 w-16 bg-gray-100 rounded animate-pulse"/>
        </div>);
}
// ── Hebel Card ──────────────────────────────────────────────────────────────
function HebelCard({ leverCategory, measurementTargets, categoryScores }) {
    if (leverCategory === null) {
        return (<div className="bg-white rounded-2xl p-5 flex flex-col items-center justify-center gap-2 border-l-4" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderLeftColor: BRAND.blue }}>
                <p className="text-xs text-gray-400">Kein Hebel ermittelt</p>
            </div>);
    }
    const entry = categoryScores[leverCategory];
    const relevantTargets = measurementTargets.filter(t => t.currentScore < 100);
    return (<div className="bg-white rounded-2xl p-5 flex flex-col items-center justify-between gap-4 border-l-4" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderLeftColor: BRAND.blue }}>
            <div className="flex flex-col items-center gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Hebel-Kategorie</p>
                {entry && <ScoreRing score={entry.score} size={120} strokeWidth={10}/>}
                <p className="text-sm font-bold text-center" style={{ color: BRAND.blueDeep }}>
                    {CATEGORY_LABELS[leverCategory] ?? leverCategory}
                </p>
            </div>
            {relevantTargets.length > 0 && (<div className="w-full flex flex-col gap-1 border-t border-gray-100 pt-3">
                    <p className="text-xs text-gray-400 mb-0.5">Messziele</p>
                    {relevantTargets.slice(0, 3).map(t => (<div key={t.categoryKey} className="flex items-baseline gap-1 text-xs">
                            <span className="text-gray-500 truncate flex-1">{t.label}</span>
                            <span className="font-bold shrink-0">{t.currentValue.toFixed(1)}</span>
                            {t.targetValue !== null ? (<>
                                    <span className="text-gray-300">→</span>
                                    <span className="text-green-600 font-bold shrink-0">{t.targetValue.toFixed(1)}</span>
                                </>) : (<span className="text-green-600">✓</span>)}
                            {t.unit && <span className="text-gray-400">{t.unit}</span>}
                        </div>))}
                </div>)}
        </div>);
}
function HebelCardPlaceholder() {
    return (<div className="bg-white rounded-2xl p-5 flex flex-col items-center justify-center gap-3 border-l-4" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderLeftColor: '#d1dfe8' }}>
            <div className="h-2.5 w-24 bg-gray-200 rounded animate-pulse"/>
            <ScoreRingPlaceholder size={120}/>
            <div className="h-4 w-28 bg-gray-200 rounded animate-pulse"/>
        </div>);
}
const HealthscoreCockpitV2 = ({ testUrl, historyUrl, reportsUrl }) => {
    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetch(generateUrl('sitzkrieger_v2_myareaapi_healthscore_v2_cockpit'), {
                    headers: { 'Accept': 'application/json' },
                    credentials: 'same-origin',
                });
                if (res.status === 404) {
                    setReport(null);
                }
                else if (!res.ok) {
                    setHasError(true);
                }
                else {
                    setReport(await res.json());
                }
            }
            catch {
                setHasError(true);
            }
            finally {
                setIsLoading(false);
            }
        };
        void fetchData();
    }, []);
    if (hasError) {
        return <></>;
    }
    const hasData = !isLoading && report !== null;
    const overallScore = report?.overallScore ?? 0;
    const leverCategory = report?.leverCategory ?? null;
    const focusCategories = report?.focusCategories ?? [];
    const categoryScores = report?.categoryScores ?? {};
    const measurementTargets = report?.measurementTargets ?? [];
    return (<div className="flex flex-col gap-3">
            {/* Top row: score | IST-Zustand | Hebel */}
            <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>

                {/* Score card */}
                <div className="bg-white rounded-2xl p-5 flex flex-col items-center justify-between gap-4" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                    <div className="flex flex-col items-center gap-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Gesamt-Score</p>
                        {isLoading ? (<ScoreRingPlaceholder size={148}/>) : (<ScoreRing score={overallScore} size={148} strokeWidth={11}/>)}
                        {isLoading ? (<div className="h-3.5 w-20 bg-gray-100 rounded animate-pulse"/>) : report !== null ? (<p className="text-xs text-gray-400">
                                {new Date(report.generatedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </p>) : (<p className="text-xs text-gray-400">Noch kein Test</p>)}
                    </div>
                    <div className="flex flex-col items-stretch gap-1.5 w-full">
                        {hasData && (<>
                                <a href={historyUrl} className="text-xs font-medium px-3 py-1.5 rounded-lg text-center" style={{ backgroundColor: BRAND.tint, color: BRAND.blue }}>
                                    Verlauf
                                </a>
                                <a href={reportsUrl} className="text-xs font-medium px-3 py-1.5 rounded-lg text-center" style={{ backgroundColor: BRAND.tint, color: BRAND.blue }}>
                                    Berichte
                                </a>
                            </>)}
                        <a href={testUrl} className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white text-center" style={{ backgroundColor: BRAND.blue }}>
                            {hasData ? 'Neuen Test starten' : 'Jetzt starten'}
                        </a>
                    </div>
                </div>

                {/* IST-Zustand card */}
                <div className="bg-white rounded-2xl p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">IST-Zustand</p>
                    <div className="flex flex-col gap-0.5">
                        {IST_ZUSTAND_CATEGORIES.map(cat => {
            const entry = categoryScores[cat];
            if (isLoading || entry === undefined)
                return <IstZustandRowPlaceholder key={cat}/>;
            return (<IstZustandRow key={cat} category={cat} entry={entry} isLever={leverCategory === cat} isFocus={focusCategories.includes(cat)}/>);
        })}
                    </div>
                </div>

                {/* Hebel card */}
                {isLoading ? (<HebelCardPlaceholder />) : report === null ? (<div className="bg-white rounded-2xl flex flex-col items-center justify-center p-5 gap-3" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                        <p className="text-sm text-gray-500 text-center max-w-xs">
                            Mach deinen ersten Healthscore-Test.
                        </p>
                        <a href={testUrl} className="text-sm font-bold px-4 py-2 rounded-lg text-white" style={{ backgroundColor: BRAND.blue }}>
                            Test starten
                        </a>
                    </div>) : (<HebelCard leverCategory={leverCategory} measurementTargets={measurementTargets} categoryScores={categoryScores}/>)}
            </div>

            {/* Verhalten: each tile is its own card */}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {VERHALTEN_CATEGORIES.map(cat => {
            const entry = categoryScores[cat];
            if (isLoading || entry === undefined)
                return <VerhaltenTilePlaceholder key={cat}/>;
            return (<VerhaltenTile key={cat} category={cat} entry={entry} isFocus={focusCategories.includes(cat)}/>);
        })}
            </div>

        </div>);
};
HealthscoreCockpitV2.displayName = 'HealthscoreCockpitV2';
export default HealthscoreCockpitV2;
